import type { FieldValuesFromFieldErrors } from '@hookform/error-message';
import { CloseCircle } from 'iconsax-react';
import type { InputHTMLAttributes } from 'react';
import {
  type Control,
  type FieldErrors,
  type FieldName,
  type FieldPathValue,
  type FieldValues,
  type Path,
  useController,
} from 'react-hook-form';
import cn from '@/utils/merge-class-names';
import { FieldMessage } from '../fields/field-message';

interface FormInputProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>
  extends InputHTMLAttributes<HTMLInputElement> {
  name: TName;
  control: Control<TFieldValues>;
  helperText?: string;
  helperTextClassName?: string;
  defaultValue: FieldPathValue<TFieldValues, TName>;
  showClearButton?: boolean;
}

export default function FormInput<TF extends FieldValues>({
  control,
  name,
  helperText,
  helperTextClassName,
  showClearButton,
  className,
  defaultValue,
  ...rest
}: FormInputProps<TF>) {
  const {
    field,
    formState: { errors },
  } = useController({ control, name, defaultValue });

  return (
    <div className={cn('relative', className)}>
      <input
        type='text'
        {...field}
        {...rest}
        className={cn(
          'input input-bordered w-full bg-neutral-0 focus:border-primary focus:outline-none',
          showClearButton && 'pr-10',
          errors[name] && 'border-error focus:border-error',
        )}
      />
      {showClearButton && field.value && (
        <button
          type='button'
          className='btn btn-ghost btn-sm absolute right-1 top-2 text-neutral-0'
          onClick={(e) => {
            e.preventDefault();
            field.onChange(defaultValue);
          }}>
          <CloseCircle
            variant='Bold'
            className='size-5 text-secondary'
          />
        </button>
      )}

      <FieldMessage
        name={name as unknown as FieldName<FieldValuesFromFieldErrors<FieldErrors<TF>>>}
        errors={errors}
        helperText={helperText}
        className={helperTextClassName}>
        {rest.maxLength ? (
          <span className='label-text-alt text-secondary'>{`${field.value?.length ?? 0}/${rest.maxLength}`}</span>
        ) : null}
      </FieldMessage>
    </div>
  );
}
